import { Component, EventEmitter, Input, Output, CUSTOM_ELEMENTS_SCHEMA, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { ButtonComponent } from '../button/button.component';
import { ButtonStyles } from 'src/app/interfaces/components/elements/Button';

@Component({
  selector: 'app-datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, FormsModule, IonicModule, ButtonComponent]
})
export class DatetimePickerComponent implements OnInit, OnChanges {
  @Input() isOpen: boolean = false;
  @Input() value: string = '';
  @Input() placeholder: string = 'Selecione data e horário';
  @Input() minDate?: string;
  @Input() maxDate?: string;
  @Input() buttonStyles?: any;

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() valueChange = new EventEmitter<string>();

  public selectedDateTime: string = '';
  public minDateValue: string = '';

  // Horário comercial: 09h até 18h (inclusivo; minutos de 18h serão clampeados para 00)
  public hourValues: number[] = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

  constructor() {}

  private getCurrentDate(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    // Mantém formato local "YYYY-MM-DDTHH:mm:ss" sem Z
    return `${year}-${month}-${day}T00:00:00`;
  }

  ngOnInit() {
    this.selectedDateTime = this.value || '';
    this.minDateValue = this.minDate || this.getCurrentDate();
  }

  ngOnChanges() {
    this.selectedDateTime = this.value || '';
  }

  public openModal(): void {
    this.selectedDateTime = this.value || '';
    this.isOpen = true;
    this.isOpenChange.emit(true);
  }

  public closeModal(): void {
    this.isOpen = false;
    this.isOpenChange.emit(false);
  }

  public confirmSelection(): void {
    // Garante que, ao confirmar, o valor esteja dentro do horário comercial
    const clamped = this.clampToBusinessHours(this.selectedDateTime);
    this.valueChange.emit(clamped);
    this.closeModal();
  }

  public cancelSelection(): void {
    this.selectedDateTime = this.value || '';
    this.closeModal();
  }

  public formatDisplayValue(value: string): string {
    if (!value) return this.placeholder;
    try {
      const date = new Date(value);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return this.placeholder;
    }
  }

  // Quando o usuário mexe no datetime, mantemos dentro de 09:00–18:00
  public onDateTimeChange(ev: any): void {
    const val = ev?.detail?.value as string;
    this.selectedDateTime = this.clampToBusinessHours(val);
  }

  /**
   * Clampa o datetime para o intervalo 09:00–18:00 do dia escolhido.
   * - Se hora < 09 => 09:00
   * - Se hora > 18 => 18:00
   * - Se hora === 18 e minuto > 0 => 18:00
   */
  private clampToBusinessHours(isoLocal: string): string {
    if (!isoLocal) return isoLocal;

    // Espera formato "YYYY-MM-DDTHH:mm[:ss]" (sem Z)
    const [datePart, timePart = '00:00:00'] = isoLocal.split('T');
    if (!datePart) return isoLocal;

    const [hh = '00', mm = '00', ss = '00'] = timePart.split(':');
    let h = parseInt(hh, 10);
    let m = parseInt(mm, 10);
    let s = parseInt(ss, 10);

    if (isNaN(h)) h = 0;
    if (isNaN(m)) m = 0;
    if (isNaN(s)) s = 0;

    if (h < 9) {
      h = 9; m = 0; s = 0;
    } else if (h > 18) {
      h = 18; m = 0; s = 0;
    } else if (h === 18 && (m > 0 || s > 0)) {
      // Permite apenas 18:00 exato
      m = 0; s = 0;
    }

    const HH = String(h).padStart(2, '0');
    const MM = String(m).padStart(2, '0');
    const SS = String(s).padStart(2, '0');

    return `${datePart}T${HH}:${MM}:${SS}`;
  }
}
